/**
 * AS-11272 M6 (decision C7) — an existing merchant moves their store to the
 * new app (atlas).
 *
 * The choice is per STORE and lives on the server, because Kong routes
 * my.assisty.ai before any page code runs: `PUT /stores/frontend-app` writes
 * the row, projects `shell:{shop}` to Redis and records who/when/`opt_in`.
 * Nothing goes to localStorage (today's per-browser opt-ins, `menuVersionChoice`
 * and `atlasFeatures`, are invisible to Kong). After the write the page leaves
 * through the M2 shell guard, which is the one place that knows how to hop
 * (`hopUrl`); a plain `location.reload()` would follow the old cookie and land
 * in warbler again.
 *
 * Only the owner sees the entry points (`store.frontendAppOptInAvailable`,
 * computed by the bot for the calling token) and only the owner's token passes
 * the endpoint; a member who gets here anyway sees the bot's message.
 */
import type { StoreModel } from '@/models/store';
import { forceHop } from '@/utils/shellGuard';

// Runtime imports stay limited to the pure shell guard: `@/modules/events`
// and `@/services/http` both reach `main.ts`, which the mocha run cannot load.
// The dialog supplies those two; the hop rule stays here.

export const OPT_IN_PATH = '/stores/frontend-app';
export const OPT_IN_EVENT = 'shell_opt_in';
/** Same shape as the other `TryNewAtlasBanner` messages ("A new … experience is available."). */
export const OPT_IN_BANNER_MESSAGE = 'A new Assisty experience is available.';

export type OptInSource = 'app_banner' | 'settings' | 'menu';

/**
 * AS-11621: the opt-in banner sits in the app shell (App.vue), not on Home,
 * which redirects to `/assistant/today` on menu V3 before anyone sees it. It
 * shows wherever the bot offers opt-in (`frontendAppOptInAvailable`: owner +
 * flag + warbler store) except where `<app-banner>` is hidden too (the atlas
 * onboarding embed; App.vue's `isAtlasOnboardingRoute` owns that rule).
 */
export function showOptInBanner(optInAvailable: boolean | undefined, appBannerHidden: boolean): boolean {
  return optInAvailable === true && !appBannerHidden;
}

export type OptInResult = { ok: true } | { ok: false; message: string };

export interface OptInDeps {
  /** `http.put`. */
  put: (url: string, data?: any) => Promise<any>;
  /** `EventHandler.track`; `options` reaches mixpanel (`transport: 'sendBeacon'` + `send_immediately` survive the hop). */
  track: (event: string, props: Record<string, unknown>, options?: Record<string, unknown>) => void;
  /** Leave for the other shell; defaults to the M2 guard, which owns the URL rule. */
  hop?: (shopName: string) => void;
}

/**
 * The store is on atlas now: hop there. Not through the guard's cooldown — a
 * merchant who arrived by a hop and opts in within 15 s must still leave.
 */
export function hopToAtlas(shopName: string): void {
  forceHop(shopName);
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole days since install; null when the date is missing or unparsable. */
export function storeAgeDays(installedAt: string | null | undefined, now: number = Date.now()): number | null {
  if (!installedAt) return null;
  const t = new Date(installedAt).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now - t) / MS_PER_DAY));
}

/** The Mixpanel payload the design asks for: plan and store age, plus where the click came from. */
export function optInProperties(store: StoreModel, source: OptInSource, now: number = Date.now()) {
  const pkg: any = store.currentPackage || {};
  const info: any = store.information || {};
  return {
    store_id: store.id,
    shop_name: store.shopName,
    package_name: pkg.name || null,
    plan_display_name: info.plan_display_name || null,
    installed_at: store.installedAt || null,
    store_age_days: storeAgeDays(store.installedAt, now),
    source,
  };
}

/** The bot answers `{ message }`; warbler's axios leaves the body a string (custom transformResponse). */
export function optInErrorMessage(error: any, fallback: string): string {
  const data = error && error.response ? error.response.data : undefined;
  if (data && typeof data === 'object' && data.message) return String(data.message);
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (parsed && parsed.message) return String(parsed.message);
    } catch {
      /* not JSON */
    }
  }
  return (error && error.message) || fallback;
}

/**
 * Write first (the only step that may fail), then track, then hop. Tracking
 * before the write would count merchants the bot refused; tracking after the
 * hop would never run.
 */
export async function optInToAtlas(
  store: StoreModel,
  source: OptInSource,
  deps: OptInDeps,
): Promise<OptInResult> {
  try {
    await deps.put(OPT_IN_PATH, { frontend_app: 'atlas' });
  } catch (error: any) {
    return { ok: false, message: optInErrorMessage(error, 'Could not switch to the new version. Please try again.') };
  }
  try {
    // Mixpanel batches by default, and the in-iframe hop below fires neither `visibilitychange` nor a
    // persisted `pagehide`. Embedded (in-memory queue) the event is lost; top-level it waits for a
    // warbler visit an opted-in store may never make. Skip the queue (AS-11622).
    deps.track(OPT_IN_EVENT, optInProperties(store, source), { transport: 'sendBeacon', send_immediately: true });
  } catch {
    /* analytics never blocks the switch */
  }
  (deps.hop || hopToAtlas)(store.shopName);
  return { ok: true };
}
